// src/context/ThemeProvider.jsx
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "era-ai-theme";
const ThemeContext = createContext(null);

/**
 * ThemeProvider
 * -------------
 * Wrap your app root with this once:
 *
 *   import { ThemeProvider } from "./context/ThemeProvider";
 *   <ThemeProvider><App /></ThemeProvider>
 *
 * Then use the hook anywhere:
 *
 *   import { useTheme } from "./context/ThemeProvider";
 *   const { themes, activeTheme, applyTheme } = useTheme();
 */
export function ThemeProvider({ children }) {
  const [themes, setThemes]           = useState([]);
  const [activeThemeId, setActiveId]  = useState(() => localStorage.getItem(STORAGE_KEY) || "pure-light");
  const [loading, setLoading]         = useState(true);

  /* ── Load themes.json once on mount ── */
  useEffect(() => {
    fetch("/themes.json")
      .then((r) => {
        if (!r.ok) throw new Error(`Could not load themes.json (${r.status})`);
        return r.json();
      })
      .then((data) => {
        setThemes(data.themes || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("[ThemeProvider]", err);
        setLoading(false);
      });
  }, []);

  /* ── Apply tokens to :root whenever themes load or activeId changes ── */
  const applyTheme = useCallback(
    (id) => {
      const theme = themes.find((t) => t.id === id);
      if (!theme) return;

      const root = document.documentElement;
      Object.entries(theme.tokens).forEach(([prop, value]) => {
        root.style.setProperty(prop, value);
      });

      /* Also update sidebar gradient, which uses custom tokens */
      root.style.setProperty(
        "--home-sidebar-gradient",
        `linear-gradient(135deg, var(--color-sidebar-from) 0%, var(--color-sidebar-to) 100%)`
      );

      setActiveId(id);
      localStorage.setItem(STORAGE_KEY, id);
    },
    [themes]
  );

  /* Apply saved theme whenever themes array is ready */
  useEffect(() => {
    if (themes.length > 0) applyTheme(activeThemeId);
  }, [themes]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTheme = themes.find((t) => t.id === activeThemeId) ?? null;

  return (
    <ThemeContext.Provider value={{ themes, activeTheme, activeThemeId, applyTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Hook — must be used inside <ThemeProvider> */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
